import axios from 'axios'
import type { Product, AuthTokenResponse } from '@/types'

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000/api/v1'

class ApiService {
  private token: string | null = null

  async authenticate(username: string = 'root', password: string = 'root'): Promise<void> {
    const formData = new URLSearchParams()
    formData.append('username', username)
    formData.append('password', password)

    const response = await axios.post<AuthTokenResponse>(
      `${API_URL}/auth/token`,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    )

    this.token = response.data.access_token
  }

  private getHeaders(required: boolean = true) {
    if (!this.token) {
      if (required) {
        throw new Error('Not authenticated')
      }
      return {}
    }
    return {
      'Authorization': `Bearer ${this.token}`
    }
  }

  async getProductByUPC(upc: string, cache: boolean = true): Promise<Product> {
    // UPC lookup doesn't require authentication
    const response = await axios.get<Product>(
      `${API_URL}/products/upc/${upc}?cache=${cache}`
    )
    return response.data
  }

  async searchProducts(query: string): Promise<Product[]> {
    // Get all products - no authentication required
    const response = await axios.get<Product[]>(`${API_URL}/products`)

    // Filter locally by name
    const allProducts = response.data
    const lowerQuery = query.toLowerCase()
    return allProducts.filter(p =>
      p.name.toLowerCase().includes(lowerQuery) ||
      p.brand?.toLowerCase().includes(lowerQuery)
    )
  }

  async getProduceItems(): Promise<Product[]> {
    // Get all produce items - no authentication required
    const response = await axios.get<Product[]>(`${API_URL}/products/produce`)
    return response.data
  }

  async getRecentProducts(limit: number = 100): Promise<Product[]> {
    // Get recently added/modified products - no authentication required
    const response = await axios.get<Product[]>(`${API_URL}/products/recent?limit=${limit}`)
    return response.data
  }

  getImageUrl(imageId: string): string {
    // Image URLs don't require authentication
    return `${API_URL}/files/${imageId}/image`
  }

  async updateProduct(productId: string, productData: Partial<Product>): Promise<Product> {
    const response = await axios.put<Product>(
      `${API_URL}/products/${productId}`,
      productData,
      { headers: this.getHeaders() }
    )
    return response.data
  }

  async deleteProduct(productId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/products/${productId}`,
      { headers: this.getHeaders() }
    )
  }

  async resetDatabase(): Promise<{ message: string; deleted: any }> {
    const response = await axios.post(
      `${API_URL}/products/reset`,
      {},
      { headers: this.getHeaders() }
    )
    return response.data
  }

  async uploadImage(file: File): Promise<{ id: string }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post<{ id: string, _id?: string }>(
      `${API_URL}/files`,
      formData,
      {
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    // Handle both 'id' and '_id' response formats
    return { id: response.data.id || response.data._id || '' }
  }

  async addImageByUrl(imageUrl: string, productId: string): Promise<string> {
    // Download image from URL and upload it
    const response = await axios.get(imageUrl, { responseType: 'blob' })
    const filename = imageUrl.split('/').pop() || 'image.jpg'
    const file = new File([response.data], filename, { type: response.data.type })
    const uploaded = await this.uploadImage(file)
    return uploaded.id
  }

  isAuthenticated(): boolean {
    return !!this.token
  }

  async login(username: string, password: string): Promise<void> {
    await this.authenticate(username, password)
  }

  logout(): void {
    this.token = null
  }
}

export const api = new ApiService()

// Don't auto-authenticate on load
// UPC lookup and images work without authentication
// Admin users must log in manually at /admin
